import { FirestoreDataConverter as FirestoreAdminDataConverter } from 'firebase-admin/firestore';
import {
  FirestoreDataConverter as FirestoreClientDataConverter,
  Timestamp,
} from 'firebase/firestore';

import { ICard } from 'types/card';
import { ICollection } from 'types/collection';
import { IStory } from 'types/story';
import { ITeam } from 'types/team';

const getCollectionConverter = () => {
  return {
    toFirestore(collection: ICollection) {
      const { meta, ...data } = collection;

      const publishedAt = meta?.publishedAt as Timestamp | string;

      return {
        ...data,
        meta: {
          ...meta,
          createdAt: meta?.createdAt ? new Date(meta.createdAt) : null,
          publishedAt: publishedAt
            ? new Date(
                typeof publishedAt === 'string'
                  ? publishedAt
                  : publishedAt.seconds * 1000,
              )
            : null,
        },
      };
    },
    fromFirestore(storySnapshot: any) {
      const { meta, ...data } = storySnapshot.data();

      return {
        id: storySnapshot.id,
        ...data,
        meta: {
          ...meta,
          createdAt: meta?.createdAt
            ? meta.createdAt.toDate().toISOString()
            : null,
          publishedAt: meta?.publishedAt
            ? meta.publishedAt.toDate().toISOString()
            : null,
        },
      } as ICollection;
    },
  };
};

export const clientCollectionConverter: FirestoreClientDataConverter<ICollection> =
  getCollectionConverter();

export const adminCollectionConverter: FirestoreAdminDataConverter<ICollection> =
  getCollectionConverter();

const getStoryConverter = () => {
  return {
    toFirestore(story: IStory) {
      const { meta, ...data } = story;

      const publishedAt = meta?.publishedAt as Timestamp | string;
      const scheduledPublishAt = meta?.scheduledPublishAt as Timestamp | string;

      return {
        ...data,
        meta: {
          ...meta,
          createdAt: meta?.createdAt ? new Date(meta.createdAt) : null,
          updatedAt: new Date(),
          publishedAt: publishedAt
            ? new Date(
                typeof publishedAt === 'string'
                  ? publishedAt
                  : publishedAt.seconds * 1000,
              )
            : null,
          scheduledPublishAt: scheduledPublishAt
            ? new Date(
                typeof scheduledPublishAt === 'string'
                  ? scheduledPublishAt
                  : scheduledPublishAt.seconds * 1000,
              )
            : null,
        },
      };
    },
    fromFirestore(storySnapshot: any) {
      const { meta, ...data } = storySnapshot.data();

      return {
        id: storySnapshot.id,
        ...data,
        meta: {
          ...meta,
          createdAt: meta?.createdAt
            ? meta.createdAt.toDate().toISOString()
            : null,
          updatedAt: meta?.updatedAt
            ? meta.updatedAt.toDate().toISOString()
            : null,
          publishedAt: meta?.publishedAt
            ? meta.publishedAt.toDate().toISOString()
            : null,
          scheduledPublishAt: meta?.scheduledPublishAt
            ? meta.scheduledPublishAt.toDate().toISOString()
            : null,
        },
      } as IStory;
    },
  };
};

export const clientStoryConverter: FirestoreClientDataConverter<IStory> =
  getStoryConverter();

export const adminStoryConverter: FirestoreAdminDataConverter<IStory> =
  getStoryConverter();

const getCardConverter = () => {
  return {
    toFirestore(card: ICard) {
      const { id, ...data } = card;
      return data;
    },
    fromFirestore(cardSnapshot: any) {
      return {
        ...(cardSnapshot.data() as ICard),
        id: cardSnapshot.id,
      };
    },
  };
};

export const clientCardConverter: FirestoreClientDataConverter<ICard> =
  getCardConverter();

export const adminCardConverter: FirestoreAdminDataConverter<ICard> =
  getCardConverter();

const getTeamConverter = () => {
  return {
    toFirestore(team: ITeam) {
      const { id, ...data } = team;
      return data;
    },
    fromFirestore(teamSnapshot: any) {
      return {
        ...(teamSnapshot.data() as ITeam),
        id: teamSnapshot.id,
      };
    },
  };
};

export const clientTeamConverter: FirestoreClientDataConverter<ITeam> =
  getTeamConverter();

export const adminTeamConverter: FirestoreAdminDataConverter<ITeam> =
  getTeamConverter();

export const clientIdConverter: FirestoreClientDataConverter<any> = {
  toFirestore(data) {
    return data;
  },
  fromFirestore(snapshot) {
    return { id: snapshot.id, ...snapshot.data() };
  },
};
